/**
 * Восстановление после выката новой версии.
 *
 * Страницы и модалки грузятся по требованию отдельными файлами, а имена этих
 * файлов содержат хэш сборки. После выката новой версии файлы старой сборки
 * исчезают с сервера, и вкладка, открытая до выката, при переходе на новую
 * страницу получает ошибку загрузки — пользователь видит белый экран или
 * сообщение об ошибке, хотя достаточно перезагрузить страницу.
 *
 * Здесь такая ошибка распознаётся и страница перезагружается один раз
 * автоматически. Повторная перезагрузка блокируется на минуту: если причина
 * не в устаревшей сборке, пользователь не попадёт в цикл перезагрузок.
 */

const RELOAD_MARK_KEY = "staleBuildReloadedAt";
const RELOAD_COOLDOWN_MS = 60_000;

const CHUNK_ERROR_PATTERNS = [
  "chunkloaderror",
  "loading chunk",
  "loading css chunk",
  "failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "importing a module script failed",
];

export const isStaleBuildError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const name = (error as { name?: string })?.name ?? "";
  const message = (error as { message?: string })?.message ?? "";
  const haystack = `${name} ${message}`.toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((pattern) => haystack.includes(pattern));
};

/**
 * Перезагружает страницу, если ошибка вызвана устаревшей сборкой.
 * Возвращает true, если перезагрузка запущена.
 */
export const reloadIfStaleBuild = (error: unknown): boolean => {
  if (!isStaleBuildError(error)) {
    return false;
  }

  try {
    const lastReload = Number(sessionStorage.getItem(RELOAD_MARK_KEY) ?? 0);

    if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      return false;
    }

    sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
  } catch {
    // Приватный режим/выключённое хранилище: перезагружаем без защиты от цикла.
  }

  window.location.reload();
  return true;
};

/** Ловит ошибки загрузки файлов сборки вне рендера (клики, переходы). */
export const listenStaleBuildErrors = (): void => {
  window.addEventListener("error", (event) => {
    reloadIfStaleBuild(event.error);
  });

  window.addEventListener("unhandledrejection", (event) => {
    reloadIfStaleBuild(event.reason);
  });
};
